import { useCallback, useEffect, useMemo, useState } from 'react';

// A card held at the top of the chat once it is scrolled to: where it has
// got to, and how long it takes to let go and come back.

/** How long a held card takes to let go: pressing down, then sliding up out of the chat. */
export const HELD_LEAVE_MS = 560;
/** How long a card held again takes to come back, after the bar has settled. */
export const HELD_RETURN_MS = 560;
export const HELD_RETURN_DELAY_MS = 140;

export const LEAVE_ANIMATION = 'heldCardLeave';
export const RETURN_ANIMATION = 'heldCardReturn';

const REDUCED_MOTION = '(prefers-reduced-motion: reduce)';

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  !!window.matchMedia?.(REDUCED_MOTION).matches;

/** Where the held card's row has got to, as the thread draws it. */
export interface HeldRow {
  id: string;
  isLeaving: boolean;
  isReturning: boolean;
  onStuck: (isStuck: boolean) => void;
}

/** How far a row must move before it counts as having moved, for rounding. */
const STUCK_SLACK_PX = 2;

/** Marks a row of the thread, so what is not one, such as the dim overlay, is passed over. */
export const THREAD_ROW_ATTRIBUTE = 'data-thread-row';

/** The row before this one in the thread, if there is one. */
const rowBefore = (el: Element) => {
  let before = el.previousElementSibling;
  while (before && !before.hasAttribute(THREAD_ROW_ATTRIBUTE)) {
    before = before.previousElementSibling;
  }
  return before;
};

/**
 * Whether a sticky row is stuck: moved down from where it would sit, just
 * under the row before it. The first row sits at the top until the chat
 * scrolls, so it is stuck once it has.
 */
export const isStuckRow = (el: HTMLElement, scroller: HTMLElement) => {
  const before = rowBefore(el);
  if (!before) return scroller.scrollTop > STUCK_SLACK_PX;
  return (
    el.getBoundingClientRect().top >
    before.getBoundingClientRect().bottom + STUCK_SLACK_PX
  );
};

/** Where a held card has got to: held, letting go, or coming back. */
export const useHeldCard = (heldCardId?: string) => {
  const [wasHeldId, setWasHeldId] = useState(heldCardId);
  const [leavingId, setLeavingId] = useState<string>();
  const [returningId, setReturningId] = useState<string>();
  // Cards let go of, so one held again comes back rather than appearing.
  const [releasedIds, setReleasedIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const [isStuck, setIsStuck] = useState(false);

  if (wasHeldId !== heldCardId) {
    setWasHeldId(heldCardId);
    if (wasHeldId) {
      setReleasedIds((ids) => new Set(ids).add(wasHeldId));
      // Letting go only shows when it was stuck; among the messages nothing moves.
      if (!heldCardId && isStuck && !prefersReducedMotion()) {
        setLeavingId(wasHeldId);
      }
    }
    if (heldCardId) {
      setLeavingId(undefined);
      if (releasedIds.has(heldCardId) && !prefersReducedMotion()) {
        setReturningId(heldCardId);
      }
    }
  }

  // Ended on a timer rather than animationend, which a hidden tab never sends.
  useEffect(() => {
    if (!leavingId) return;
    const timer = setTimeout(() => setLeavingId(undefined), HELD_LEAVE_MS);
    return () => clearTimeout(timer);
  }, [leavingId]);
  useEffect(() => {
    if (!returningId) return;
    const timer = setTimeout(
      () => setReturningId(undefined),
      HELD_RETURN_DELAY_MS + HELD_RETURN_MS,
    );
    return () => clearTimeout(timer);
  }, [returningId]);

  const rowId = heldCardId ?? leavingId;
  const row = useMemo<HeldRow | undefined>(
    () =>
      rowId
        ? {
            id: rowId,
            isLeaving: rowId === leavingId,
            isReturning: rowId === returningId,
            onStuck: setIsStuck,
          }
        : undefined,
    [leavingId, returningId, rowId],
  );

  /** Whether a card is or was held, so it takes the lift and the dimming. */
  const isHoldable = useCallback(
    (id: string) =>
      id === heldCardId || id === leavingId || releasedIds.has(id),
    [heldCardId, leavingId, releasedIds],
  );
  /** Lifted once it has reached the top and landed there. */
  const isLifted = useCallback(
    (id: string) => id === heldCardId && isStuck && id !== returningId,
    [heldCardId, isStuck, returningId],
  );

  return { row, isHoldable, isLifted };
};
